import React, { useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Header } from './components/Header';
import { SearchPanel } from './components/SearchPanel';
import { ResultsCounter } from './components/ResultsCounter';
import { CompanyList } from './components/CompanyList';
import { CompanyDetail } from './components/CompanyDetail';
import { LoginPage } from './components/LoginPage';
import { ProfilePanel } from './components/ProfilePanel';
import { allCompanies } from './data/mockCompanies';
import { Company, SearchMode } from './types/company';
export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [searchMode, setSearchMode] = useState<SearchMode>('keyword');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [textQuery, setTextQuery] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const filteredCompanies = useMemo(() => {
    if (!isSearchActive) return allCompanies;
    if (searchMode === 'keyword' && keywords.length > 0) {
      return allCompanies.filter(company => {
        const searchableText = [company.name, company.nameChinese, company.industry, company.subIndustry, company.city, company.province, company.description, ...company.tags, ...company.keyProducts].join(' ').toLowerCase();
        return keywords.every(keyword => searchableText.includes(keyword.toLowerCase()));
      });
    }
    if (searchMode === 'text' && textQuery) {
      const query = textQuery.toLowerCase();
      // Simple NLP-like parsing
      const cityMatches = allCompanies.map(c => c.city.toLowerCase()).filter(city => query.includes(city));
      const industryMatches = allCompanies.map(c => c.industry.toLowerCase()).filter(industry => query.includes(industry.toLowerCase()));
      // Extract numbers for patent/employee filters
      const numberMatch = query.match(/(\d+)\s*(patents?|employees?)/i);
      const patentThreshold = query.includes('patent') && numberMatch ? parseInt(numberMatch[1]) : null;
      const employeeThreshold = query.includes('employee') && numberMatch ? parseInt(numberMatch[1]) : null;
      // Extract year for founded filter
      const yearMatch = query.match(/after\s*(\d{4})|founded.*?(\d{4})/i);
      const foundedAfter = yearMatch ? parseInt(yearMatch[1] || yearMatch[2]) : null;
      return allCompanies.filter(company => {
        let matches = true;
        // City filter
        if (cityMatches.length > 0) {
          matches = matches && cityMatches.includes(company.city.toLowerCase());
        }
        // Industry filter
        if (industryMatches.length > 0) {
          matches = matches && industryMatches.some(ind => company.industry.toLowerCase().includes(ind) || company.subIndustry.toLowerCase().includes(ind));
        }
        // Patent threshold
        if (patentThreshold) {
          matches = matches && company.patents >= patentThreshold;
        }
        // Employee threshold
        if (employeeThreshold) {
          matches = matches && company.employees >= employeeThreshold;
        }
        // Founded after
        if (foundedAfter) {
          matches = matches && company.founded >= foundedAfter;
        }
        // Fallback: general text search if no specific filters matched
        if (cityMatches.length === 0 && industryMatches.length === 0 && !patentThreshold && !employeeThreshold && !foundedAfter) {
          const searchableText = [company.name, company.nameChinese, company.industry, company.subIndustry, company.city, company.description, ...company.tags].join(' ').toLowerCase();
          const queryWords = query.split(/\s+/).filter(w => w.length > 2);
          matches = queryWords.some(word => searchableText.includes(word));
        }
        return matches;
      });
    }
    return allCompanies;
  }, [isSearchActive, searchMode, keywords, textQuery]);
  const handleKeywordSearch = () => {
    if (keywords.length > 0) {
      setIsSearchActive(true);
    }
  };
  const handleTextSearch = (query: string) => {
    setTextQuery(query);
    setIsSearchActive(true);
  };
  const handleModeChange = (mode: SearchMode) => {
    setSearchMode(mode);
    setIsSearchActive(false);
    setKeywords([]);
    setTextQuery('');
  };
  const showList = isSearchActive && filteredCompanies.length <= 50;
  if (!isAuthenticated) {
    return <LoginPage onLogin={() => setIsAuthenticated(true)} data-id="element-0" />;
  }
  return <div className="min-h-screen bg-slate-950" data-id="element-1">
      <Header onProfileClick={() => setShowProfile(true)} data-id="element-2" />

      <main className="max-w-5xl mx-auto px-6 py-8" data-id="element-3">
        {/* Search Section */}
        <section className="mb-8" data-id="element-4">
          <SearchPanel mode={searchMode} onModeChange={handleModeChange} keywords={keywords} onKeywordsChange={setKeywords} onKeywordSearch={handleKeywordSearch} onTextSearch={handleTextSearch} data-id="element-5" />
        </section>

        {/* Results Section */}
        <section data-id="element-6">
          {!showList ? <ResultsCounter total={allCompanies.length} filtered={filteredCompanies.length} isSearchActive={isSearchActive} data-id="element-7" /> : <CompanyList companies={filteredCompanies} onSelectCompany={setSelectedCompany} data-id="element-8" />}
        </section>
      </main>

      {/* Company Detail Slide-over */}
      <AnimatePresence data-id="element-9">
        {selectedCompany && <CompanyDetail company={selectedCompany} onClose={() => setSelectedCompany(null)} data-id="element-10" />}
      </AnimatePresence>

      {/* Profile Panel */}
      <AnimatePresence data-id="element-11">
        {showProfile && <ProfilePanel onClose={() => setShowProfile(false)} onLogout={() => {
        setShowProfile(false);
        setIsAuthenticated(false);
      }} data-id="element-12" />}
      </AnimatePresence>
    </div>;
}